import React from "react";
import clsx from "clsx";
import { ArrowRight, XCircle } from "lucide-react";
import { Button } from "../../../../ui/index.ts";
import styles from "./rejected-status-view.module.css";

interface RejectedStatusViewProps {
    approverComment?: string | null;
    loading?: boolean;
    onResign: () => void;
    updatedAt?: string | null;
}

const FRIDAY = 5;
const TUESDAY = 2;

function getRejectedProceedAt(updatedAt: string | null | undefined): number | null {
    if (!updatedAt) return null;

    const updatedDate = new Date(updatedAt);
    const updatedTime = updatedDate.getTime();
    if (Number.isNaN(updatedTime)) return null;

    if (updatedDate.getDay() === FRIDAY) {
        const proceedDate = new Date(updatedDate);
        const daysUntilTuesday = (7 - FRIDAY) + TUESDAY;
        proceedDate.setDate(proceedDate.getDate() + daysUntilTuesday);
        return proceedDate.getTime();
    }

    const proceedDate = new Date(updatedDate);
    proceedDate.setDate(proceedDate.getDate() + 2);
    proceedDate.setHours(0, 0, 0, 0);
    return proceedDate.getTime();
}

export function RejectedStatusView({
    approverComment,
    loading = false,
    onResign,
    updatedAt,
}: RejectedStatusViewProps) {
    const [now, setNow] = React.useState(() => Date.now());
    const proceedAt = React.useMemo(() => getRejectedProceedAt(updatedAt), [updatedAt]);
    const showProceedButton = proceedAt != null && now >= proceedAt;
    const comment = approverComment?.trim();

    React.useEffect(() => {
        setNow(Date.now());
    }, [proceedAt]);

    React.useEffect(() => {
        if (proceedAt == null || now >= proceedAt) return;

        const delay = Math.max(proceedAt - now, 0);
        const timer = window.setTimeout(() => setNow(Date.now()), delay);

        return () => window.clearTimeout(timer);
    }, [now, proceedAt]);

    return (
      <div className={styles.content}>
        <div className={styles.iconWrap} aria-hidden>
          <XCircle size={40} strokeWidth={1.75} />
        </div>
        <h2 className={clsx("heading-lg", styles.title)}>
          Offboarding Request Rejected
        </h2>
        <p className={clsx("sub-heading-md", styles.message)}>
          Your offboarding request was not approved.
        </p>
        {comment ? (
          <div className={styles.commentBox}>
            <span className={styles.commentLabel}>Approver Comment</span>
            <p className={clsx("body-sm", styles.commentText)}>{comment}</p>
          </div>
        ) : null}
        {showProceedButton ? (
          <Button variant="danger-ghost" onClick={onResign} disabled={loading}>
            Proceed with Offboarding
            <ArrowRight size={16} />
          </Button>
        ) : proceedAt != null ? (
          <p className={clsx("body-sm", styles.hint)}>
            You may access Offboarding request form again after the completion of the next working day.
          </p>
        ) : null}
      </div>
    );
}
